import { Skeleton, Tab, Tabs, Typography } from "@mui/material";
import { Box, useTheme } from "@mui/system";
import { useContext, useEffect, useRef, useState } from "react";
import ActivationsKeywordExact from "./keyword/exact";
import { CountryFilter, useCountryData } from "src/components/competitive-intelligence";
import { CompetitiveIntelligenceContext } from "src/components/competitive-intelligence/components/CompetitiveIntelligenceProvider";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import countriesJson from "src/utils/countries.json";
import Iconify from "src/components/iconify";
import ActivationsKeywordOther from "./keyword/other";
import { Helmet } from "react-helmet-async";

export default function ActivationsOverview() {
    const theme = useTheme();
    const [tab, setTab] = useState("keywords_exact");
    const workspace = useCurrentWorkspace();
    const { countriesData, isLoading } = useCountryData();
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const ciContext = useContext(CompetitiveIntelligenceContext);
    const prefilledFromQueryRef = useRef<boolean>(false);

    useEffect(() => {
        if (ciContext?.selectedCountry != null) {
            setSelectedCountry(ciContext.selectedCountry);
            return;
        }

        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country') || params.get('c');

        if (codeParam && countriesData.length > 0) {
            const mapped = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
            if (mapped && countriesData.some((c) => c.country_name === mapped)) {
                prefilledFromQueryRef.current = true;
                setSelectedCountry(mapped);
                return;
            }
        }

        if (!prefilledFromQueryRef.current && !selectedCountry && countriesData.length > 0) {
            setSelectedCountry(countriesData[0].country_name);
        }

    }, [ciContext?.selectedCountry, location.search, countriesData]);

    useEffect(() => {
        localStorage.removeItem("columnVisibilityModel");
    }, []);

    return (
        <>
        <Helmet>
            <title>Activations</title>
        </Helmet>
            <Box>
                {isLoading
                    ? <Box px={"24px"}>
                        <Skeleton width={'100%'} height={'100%'} sx={{ borderRadius: 1 }} variant="rectangular" animation={"wave"} >
                            <>
                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                <Typography color={"success.main"}>Active</Typography>
                            </>
                        </Skeleton>
                    </Box>
                    : <CountryFilter
                        countries={countriesData}
                        selectedCountry={selectedCountry}
                        onCountryChange={(c) => {
                            setSelectedCountry(c);
                            try { ciContext?.setSelectedCountry?.(c); } catch { }
                            try {
                                if (workspace?.id) {
                                    const key = `ci.selectedCountry.${workspace.id}`;
                                    if (c) localStorage.setItem(key, c);
                                    else localStorage.removeItem(key);
                                }
                            } catch { }
                        }}
                    />
                }

            </Box>

            <Box px={"24px"}>
                <Box mt={"20px"} bgcolor={"white"} borderRadius={"12px"} sx={{ border: "1px solid rgba(145, 158, 171, 0.2)" }}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        sx={{
                            padding: "16px 32px",
                            "& .MuiTabs-indicator": {
                                backgroundColor: "rgba(118, 85, 193, 1)",
                                height: 2,
                            },
                            borderBottom: "1px dashed rgba(145, 158, 171, 0.20)"
                        }}
                    >
                        <Tab
                            value="keywords_exact"
                            label="Keyword (Exact)"
                            sx={{
                                "&.Mui-selected": {
                                    color: "rgba(118, 85, 193, 1)",
                                },
                            }}
                        />
                        <Tab
                            value="keywords_other"
                            label="Keyword (Other)"
                            sx={{
                                "&.Mui-selected": {
                                    color: "rgba(118, 85, 193, 1)",
                                },
                            }}
                        />
                    </Tabs>

                    <Box>
                        {tab === "keywords_exact" && (
                            selectedCountry ? (
                                <ActivationsKeywordExact country={selectedCountry} />
                            ) : (
                                <Skeleton
                                    variant="rectangular"
                                    height={400}
                                    width="100%"
                                    animation="wave"
                                />
                            )
                        )}
                        {tab === "keywords_other" && (
                            selectedCountry ? (
                                <ActivationsKeywordOther country={selectedCountry} />
                            ) : (
                                <Skeleton
                                    variant="rectangular"
                                    height={400}
                                    width="100%"
                                    animation="wave"
                                />
                            )
                        )}
                    </Box>

                </Box>
            </Box>
        </>
    );
}